import { useState, useEffect, useCallback } from "react";
import "./Management.css";
import { FaLinkedin, FaTimes } from "react-icons/fa";
import Footer from "../Component/NewFooter";
import SplashScreen from "../Component/SplashScreen";
import axios from "axios";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
// Distinct from Admin/pages/Leadership.jsx's "jeyshawn_management_data" key —
// that file caches the raw { members } API payload, this page caches a
// reshaped { bannerImage, backgroundImage, teamMembers } object. Sharing a
// key would let either page overwrite the other's cache with an incompatible shape.
const CACHE_KEY      = "jeyshawn_leadership_public_data";

const resolveSrc = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return fallback;
};

/* ── Helpers ───────────────────────────────────────────────────── */
function parseManagementData(db) {
  const members = Array.isArray(db?.members) ? db.members : [];

  return {
    bannerImage:     resolveSrc(db?.bannerImage)     || "",
    backgroundImage: resolveSrc(db?.backgroundImage) || "",
    teamMembers: members.map((m) => ({
      id:       m?._id || Math.random().toString(36).substr(2, 9),
      name:     m?.name || "",
      role:     m?.role || "",
      linkedin: m?.linkedin || "#",
      featured: !!m?.featured,
      bio:      m?.bio || "",
      imgUrl:   resolveSrc(m?.img) || null,
    })),
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

function MemberModal({ member, onClose }) {
  if (!member) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal__close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
        <div className="modal__left">
          {member.imgUrl && (
            <img
              src={member.imgUrl}
              alt={member.name || "Team member"}
              className="modal__img"
            />
          )}

          <a href={member.linkedin || "#"}
            className="modal__linkedin"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin />
          </a>
        </div>
        <div className="modal__right">
          <h2 className="modal__name">{member.name}</h2>
          <p className="modal__role">{member.role}</p>
          <div className="modal__bio">
            {(member.bio || "").split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberCard({ member, onClick }) {
  if (!member) return null;

  return (
    <div
      className={`member-card${member.featured ? " member-card--featured" : ""}`}
      onClick={() => onClick(member)}
      style={{ cursor: "pointer" }}
    >
      <div className="member-card__img-wrap">
        {member.imgUrl && (
          <img
            src={member.imgUrl}
            alt={member.name || "Team member"}
            className="member-card__img"
          />
        )}
      </div>
      <div className="member-card__body">
        <h3 className="member-card__name">{member.name}</h3>
        <p className="member-card__role">{member.role}</p>
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
export default function ManagementTeam() {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);
  const [selected, setSelected] = useState(null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/management`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseManagementData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/management:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;
  const teamMembers = data?.teamMembers || [];
  const hasContent  = teamMembers.length > 0;
  const featured = teamMembers.filter((m) => m?.featured);
  const rest     = teamMembers.filter((m) => !m?.featured);

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carriersmg">
        {data?.bannerImage && <img src={data.bannerImage} alt="" />}
      </div>

      {hasContent && (
        <section className="management-team" style={data.backgroundImage ? { backgroundImage: `url(${data.backgroundImage})` } : undefined}>
          <div className="management-team__header">
            <h2 className="management-team__title">Management Team</h2>
            <span className="management-team__divider" />
          </div>

          <div className="management-team__featured-row">
            {featured.map((m) => (
              <MemberCard key={m.id} member={m} onClick={setSelected} />
            ))}
          </div>

          <div className="management-team__grid">
            {rest.map((m) => (
              <MemberCard key={m.id} member={m} onClick={setSelected} />
            ))}
          </div>

          <MemberModal member={selected} onClose={() => setSelected(null)} />
        </section>
      )}

      <Footer />
    </>
  );
}
